/**
 * Where a countdown pill goes when someone taps it.
 *
 * Bobbie, 2026-08-27: "when you click on the pull [pill] button as a parent or
 * staff for an important date it should take you to the page/event for it if
 * there is one!"
 *
 * A COMPETITION has a real page of its own - name, when, where, RSVPs, the
 * board posts scoped to it - at /comp-board/events/:id, and that page runs the
 * same countdownAudienceAllows check the pill did, so a link can never show
 * someone a date they were not allowed to see.
 *
 * Everything else opens /events/:id, the generic event page every viewer can
 * reach (Bobbie, 2026-10-18: "on the pill buttons that have the event names
 * when you click on them it should take you to that event page"). Recitals
 * and other dates used to drop a person on the Important dates list, because
 * they had no page of their own; now they do. That page runs the same
 * countdownAudienceAllows check, so it cannot widen who sees what either.
 *
 * `compPage: false` sends a competition to /events/:id too. The comp page is
 * for the comp team: a family with no comp dancer tapping an 'everyone'
 * competition would land on "This board is for the comp team" instead of the
 * date. Callers that know the viewer (the Events list, the class and calendar
 * pills) pass it; leaving it out keeps the competition answer it always had.
 *
 * Null is deliberately possible for callers that want "no link at all" for an
 * event with no id, so nothing renders a dead pill.
 *
 * WHY IT SITS IN A FILE OF ITS OWN. countdownEvents.ts imports React and the
 * Firebase SDK, so a test cannot load it, and the test written for this
 * function settled for five regular expressions over the source instead. PR
 * #739 review, 2026-08-27, proved what that was worth by swapping the two
 * destinations - every pill in the Hub pointing at the wrong page - and
 * watching the suite stay green. This file imports nothing, so the test can
 * call the function and read the answer. countdownEvents.ts re-exports it, so
 * every existing import keeps working.
 */

export type CountdownHrefKind = 'competition' | 'recital' | 'other';

export interface CountdownEventHrefOptions {
  /** False when the viewer cannot open the comp team page. Absent = true. */
  compPage?: boolean;
}

export function countdownEventHref(
  event: { id?: string | null; kind?: CountdownHrefKind | null },
  options: CountdownEventHrefOptions = {}
): string | null {
  if (!event.id) return null;
  const id = encodeURIComponent(event.id);
  return event.kind === 'competition' && options.compPage !== false
    ? `/comp-board/events/${id}`
    : `/events/${id}`;
}
